import { Building2, Mail, MapPin, User, Hash, Pencil, Activity, Droplet, CloudRain } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Company } from "@/types/company/company.types";
import { useLanguage } from "@/contexts/LanguageContext";
import { UserClaims } from "@/types/auth/auth.types";
import { useState } from "react";

interface CompanyInfoProps {
  company: Company;
  onEdit: () => void;
  claims: UserClaims | null;
}

export const CompanyInfo = ({ company, onEdit, claims }: CompanyInfoProps) => {
  const { t } = useLanguage();
  const [logoError, setLogoError] = useState(false);

  const handleLogoError = () => {
    setLogoError(true);
  };

  // Helper function to display placeholder for empty values
  const displayValue = (...values: (string | number | undefined)[]) => {
    const nonEmptyValue = values.find((value) => value !== undefined && value !== null && value !== "");

    if (nonEmptyValue === undefined) {
      return <span className="text-gray-400">—</span>;
    }
    return nonEmptyValue;
  };


  return (
    <div className="space-y-6">
      <Card className="p-4">
        <div className="flex -mx-4 -my-4">
          {/* Logo Section */}
          <div className="w-[160px] min-h-[160px] flex-shrink-0 flex items-center justify-center border-r border-gray-200 px-4">
            <div className="w-24 h-24 relative flex items-center justify-center">
              {!company.logoUrl || logoError ? (
                <div className="flex items-center justify-center w-full h-full">
                  <Building2 className="w-12 h-12 text-gray-400" />
                </div>
              ) : (
                <img
                  src={company.logoUrl}
                  onError={handleLogoError}
                  alt={company.name}
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>
          </div>

          {/* Company Information */}
          <div className="flex-grow grid grid-cols-2 sm:grid-cols-3 gap-4 px-10 py-6 min-h-[160px] relative">
            {(claims?.role === "assimagra" || claims?.role === "get2c" || (claims?.role === "company" && claims?.companyId === company?.id)) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="default"
                      size="icon"
                      className="absolute top-3 right-3 bg-teal-600/80 hover:bg-teal-600/90 rounded-full h-9 w-9"
                      onClick={onEdit}
                    >
                      <Pencil className="h-4 w-4 text-white" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left" align="center">
                    <p>{t("dashboard.companyInfo.editTooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Associate Number */}
            <div>
              <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                <Hash className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{t("companyForm.fields.associateNumber")}</span>
              </div>
              <p className="text-base pl-7">{displayValue(company.associateNumber ?? undefined)}</p>
            </div>

            {/* Name */}
            <div>
              <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                <Building2 className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{t("dashboard.companyInfo.name")}</span>
              </div>
              <p className="text-base pl-7">{displayValue(company.name)}</p>
            </div>

            {/* NIF */}
            <div>
              <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                <Hash className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{t("dashboard.companyInfo.nif")}</span>
              </div>
              <p className="text-base pl-7">{displayValue(company.nif)}</p>
            </div>

            {/* Activity Type */}
            <div>
              <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                <Activity className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{t("companyForm.fields.activityType")}</span>
              </div>
              <p className="text-base pl-7">
                {company.activityType ? (
                  t(`activityTypes.${company.activityType}`)
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </p>
            </div>

            {/* Basin and Weather Station - Conditional rendering based on activity type */}
            {company.activityType === "extraction_and_transformation" ? (
              <>
                {/* Extraction Basin */}
                <div>
                  <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                    <Droplet className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">{t("companyForm.installations.extraction.title")} - {t("companyForm.fields.basinReference")}</span>
                  </div>
                  <p className="text-base pl-7">{displayValue(company.extractionBasinName)}</p>
                </div>

                {/* Extraction Weather Station */}
                <div>
                  <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                    <CloudRain className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">{t("companyForm.installations.extraction.title")} - {t("companyForm.fields.weatherStationReference")}</span>
                  </div>
                  <p className="text-base pl-7">{displayValue(company.extractionWeatherStationName)}</p>
                </div>

                {/* Transformation Basin */}
                <div>
                  <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                    <Droplet className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">{t("companyForm.installations.transformation.title")} - {t("companyForm.fields.basinReference")}</span>
                  </div>
                  <p className="text-base pl-7">{displayValue(company.transformationBasinName)}</p>
                </div>

                {/* Transformation Weather Station */}
                <div>
                  <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                    <CloudRain className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">{t("companyForm.installations.transformation.title")} - {t("companyForm.fields.weatherStationReference")}</span>
                  </div>
                  <p className="text-base pl-7">{displayValue(company.transformationWeatherStationName)}</p>
                </div>
              </>
            ) : (
              <>
                {/* Single Activity Basin */}
                <div>
                  <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                    <Droplet className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">{t("companyForm.fields.basinReference")}</span>
                  </div>
                  <p className="text-base pl-7">{displayValue(company.basinName)}</p>
                </div>

                {/* Single Activity Weather Station */}
                <div>
                  <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                    <CloudRain className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">{t("companyForm.fields.weatherStationReference")}</span>
                  </div>
                  <p className="text-base pl-7">{displayValue(company.weatherStationName)}</p>
                </div>
              </>
            )}

            {/* Address */}
            <div>
              <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                <MapPin className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{t("dashboard.companyInfo.address")}</span>
              </div>
              <p className="text-base pl-7">{displayValue(company.address)}</p>
            </div>

            {/* Calculation Manager */}
            <div>
              <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                <User className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{t("dashboard.companyInfo.manager")}</span>
              </div>
              <p className="text-base pl-7">{displayValue(company.calculationManager)}</p>
            </div>

            {/* Manager Email */}
            <div>
              <div className="flex items-center gap-2 text-teal-600 mb-0.5">
                <Mail className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{t("dashboard.companyInfo.managerEmail")}</span>
              </div>
              <p className="text-base pl-7">{displayValue(company.calculationManagerEmail)}</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
